/**
 * Legal acknowledgement contracts (#4301).
 *
 * Two tiers, deliberately distinct:
 *
 * - `room_entry` (Tier 2) — every participant, on joining any room, accepts and
 *   waives Telegames' liability for what transpires there, expressly including
 *   AI companion output.
 * - `desktop_share` / `recording` / `streaming` (Tier 1) — the acting user takes
 *   responsibility for the content they put on screen, into storage, or onto a
 *   third-party destination, and affirms they hold the rights to it.
 *
 * ## The clause text below is PLACEHOLDER
 *
 * `LEGAL_ACKNOWLEDGEMENT_COPY` is engineering's stand-in so the mechanism can be
 * built, reviewed and merged while counsel drafts the real wording. It is NOT a
 * reviewed legal instrument. Replacing it is a text edit plus a
 * `LEGAL_TERMS_VERSION` bump — every stored acknowledgement records the version
 * it was given, so a bump re-prompts everyone automatically.
 */

export const LEGAL_CAPABILITIES = [
  'room_entry',
  'desktop_share',
  'recording',
  'streaming',
] as const;

export type LegalCapability = typeof LEGAL_CAPABILITIES[number];

export function isLegalCapability(value: unknown): value is LegalCapability {
  return typeof value === 'string' && (LEGAL_CAPABILITIES as readonly string[]).includes(value);
}

/**
 * Version of the acknowledgement text a user accepted.
 *
 * Bump this whenever `LEGAL_ACKNOWLEDGEMENT_COPY` changes in substance. Stored
 * acknowledgements carry the version, so a bump invalidates prior acceptances
 * and re-prompts without a migration or a backfill.
 *
 * Format: `YYYY-MM-DD.N` — date the text was settled, revision within that day.
 */
export const LEGAL_TERMS_VERSION = '2026-09-21.0-placeholder';

/**
 * How long an acknowledgement stands before the user is asked again.
 *
 * Matches the adult-content interstitial precedent (#1663). Scoped per room and
 * per capability: a host who runs the same room daily is not re-prompted, a new
 * room is a fresh prompt.
 */
export const LEGAL_ACK_TTL_DAYS = 30;
export const LEGAL_ACK_TTL_MS = LEGAL_ACK_TTL_DAYS * 24 * 60 * 60 * 1000;

export interface LegalAcknowledgementCopy {
  /** Dialog heading. */
  title: string;
  /** One-line framing shown under the heading. */
  intro: string;
  /** The substantive clauses, rendered as a list. */
  clauses: readonly string[];
  /** Text of the accept control. */
  acceptLabel: string;
  /** Text of the decline control. */
  declineLabel: string;
}

/**
 * #4887 — only a room whose engine records and streams (Make a show) says so at the door. A
 * standard room cannot record or stream, so telling everyone entering it that it might is wrong.
 */
export const ROOM_ENTRY_RECORDING_CLAUSE =
  'This room may be recorded or streamed by its host. If it is, you will see an indicator in the room.';

export const LEGAL_ACKNOWLEDGEMENT_COPY: Record<LegalCapability, LegalAcknowledgementCopy> = {
  room_entry: {
    title: 'Before you enter this room',
    intro:
      'This room may include AI companions and other participants. By entering, you agree to the following.',
    clauses: [
      'AI companions are artificial intelligence. What they say is generated, may be inaccurate, offensive, or unsuitable, and is not a statement, recommendation, or advice from Telegames.',
      'Telegames does not control, review, or pre-screen what other participants say, show, or do in this room.',
      'To the maximum extent permitted by law, you release and waive Telegames from liability for anything that transpires in this room, including conversations with AI companions.',
      ROOM_ENTRY_RECORDING_CLAUSE,
    ],
    acceptLabel: 'I understand — enter room',
    declineLabel: 'Leave',
  },
  desktop_share: {
    title: 'Sharing your desktop',
    intro:
      'You are about to put your screen in front of everyone in this room. You are responsible for what appears on it.',
    clauses: [
      'You are solely responsible for all content you share, including anything visible on a shared screen, window, or tab.',
      'Telegames has no knowledge of what your shared screen contains and does not review, monitor, or pre-screen it.',
      'You represent that you hold the rights, licenses, and permissions necessary to share this content, including any copyright and trademark implications, and the consent of anyone who appears in it.',
      'You indemnify Telegames against claims arising from the content you share.',
    ],
    acceptLabel: 'I accept — start sharing',
    declineLabel: 'Cancel',
  },
  recording: {
    title: 'Recording this room',
    intro:
      'You are about to record this room. The recording is stored by Telegames and is yours to download.',
    clauses: [
      'You are solely responsible for all content you record, including anything shared by other participants or shown on a shared screen.',
      'Telegames has no knowledge of what a recording contains and does not review, monitor, or pre-screen it.',
      'You represent that you hold the rights, licenses, and permissions necessary to record this content, including any copyright and trademark implications, and the consent of everyone captured in it.',
      'You indemnify Telegames against claims arising from the content you record.',
    ],
    acceptLabel: 'I accept — start recording',
    declineLabel: 'Cancel',
  },
  streaming: {
    title: 'Streaming this room',
    intro:
      'You are about to send this room to a destination you have chosen. That stream leaves Telegames.',
    clauses: [
      'You are solely responsible for all content you stream, including anything shared by other participants or shown on a shared screen.',
      'Telegames has no knowledge of what an outbound stream contains and does not review, monitor, or pre-screen it.',
      'You represent that you hold the rights, licenses, and permissions necessary to stream this content, including any copyright and trademark implications, and the consent of everyone captured in it.',
      'You are responsible for complying with the terms of the destination you stream to.',
      'You indemnify Telegames against claims arising from the content you stream.',
    ],
    acceptLabel: 'I accept — start streaming',
    declineLabel: 'Cancel',
  },
};

/**
 * The copy to show for a capability. `recordsAndStreams: false` drops the room-entry recording
 * clause; leave it undefined when the room's engine is unknown, and the clause stays.
 */
export function legalAcknowledgementCopyFor(
  capability: LegalCapability,
  options: { recordsAndStreams?: boolean } = {},
): LegalAcknowledgementCopy {
  const copy = LEGAL_ACKNOWLEDGEMENT_COPY[capability];
  if (capability !== 'room_entry' || options.recordsAndStreams !== false) return copy;
  return { ...copy, clauses: copy.clauses.filter(clause => clause !== ROOM_ENTRY_RECORDING_CLAUSE) };
}

// ---------------------------------------------------------------------------
// API contracts — GET/POST /api/rooms/[roomId]/legal-acknowledgement
// ---------------------------------------------------------------------------

export interface LegalAcknowledgementRecord {
  capability: LegalCapability;
  termsVersion: string;
  acknowledgedAt: string;
}

export interface LegalAcknowledgementGetResponse {
  /** Version the client should present and post back. */
  termsVersion: string;
  /**
   * Capabilities already acknowledged at `termsVersion` and still within TTL.
   * A capability absent from this list must be prompted before it proceeds.
   */
  acknowledged: LegalCapability[];
  /** Full records for the acknowledged capabilities, for display/debugging. */
  records: LegalAcknowledgementRecord[];
}

export interface LegalAcknowledgementPostBody {
  capability: LegalCapability;
  /**
   * Version the client actually displayed. The server rejects a mismatch with
   * the current version rather than recording consent to text nobody saw.
   */
  termsVersion: string;
}

/**
 * Header carrying an unauthenticated participant's stable handle, ignored when
 * the request has a session. Talk-show and townhall rooms admit guests, and a
 * waiver with no recorded subject is not worth storing.
 *
 * A header, not a query parameter: this value identifies the subject of a legal
 * record, and a query string is copied into Traefik and CDN access logs, where
 * reading it would let anyone impersonate that subject or enumerate the rooms
 * they entered.
 */
export const LEGAL_GUEST_REF_HEADER = 'x-legal-guest-ref';

/** Max length of a guest ref, matching `legal_acknowledgements.session_ref`. */
export const LEGAL_GUEST_REF_MAX_LENGTH = 128;

export interface LegalAcknowledgementPostResponse {
  capability: LegalCapability;
  termsVersion: string;
  acknowledgedAt: string;
}

export enum LegalAcknowledgementErrorCode {
  INVALID_ROOM = 'INVALID_ROOM',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  STALE_TERMS_VERSION = 'STALE_TERMS_VERSION',
  NO_IDENTITY = 'NO_IDENTITY',
  RATE_LIMITED = 'RATE_LIMITED',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}

export interface LegalAcknowledgementErrorResponse {
  error: string;
  code: LegalAcknowledgementErrorCode;
  /** Present on STALE_TERMS_VERSION so the client can re-prompt at the new text. */
  currentTermsVersion?: string;
}

/**
 * True when `acknowledgedAt` is still inside the TTL window.
 *
 * Shared by the API (deciding what to return) and the client (deciding whether
 * to prompt), so the two cannot drift.
 */
export function isLegalAcknowledgementCurrent(
  acknowledgedAt: string | Date,
  now: Date = new Date(),
): boolean {
  const ts = acknowledgedAt instanceof Date ? acknowledgedAt.getTime() : Date.parse(acknowledgedAt);
  if (!Number.isFinite(ts)) return false;
  const age = now.getTime() - ts;
  return age >= 0 && age < LEGAL_ACK_TTL_MS;
}
